import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSearchProductSuppliersMutation } from '@app/api/Products';
import type { ModelProductSuppliersSearchResult } from '@app/api/Products';
import { EDIT_SUPPLIER_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import {
  getSuppliersColumns,
  initialFilters,
} from '@app/pages/Warehouses/Suppliers/data';
import type {
  SuppliersFilters,
  UnsavedSuppliersFilters,
} from '@app/pages/Warehouses/Suppliers/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { PaginationProps } from 'antd';
import type { ChangeEventHandler } from 'react';

export const useSuppliers = () => {
  const navigate = useNavigate();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [filters, setFilters] = useState<SuppliersFilters>(
    () => initialFilters
  );

  const [unsavedFilters, setUnsavedFilters] = useState<UnsavedSuppliersFilters>(
    { SearchTerm: '' }
  );

  const {
    userAccessRights: {
      Warehouse: { IsSuppliers, IsSupplierEdit },
    },
  } = useNewUserAccessRights();

  const isPageForbidden = useMemo(() => !IsSuppliers, [IsSuppliers]);

  const [searchProductSuppliers, { isLoading: isFetching, isUninitialized }] =
    useSearchProductSuppliersMutation();

  const [data, setData] = useState<ModelProductSuppliersSearchResult | null>(
    null
  );

  const onSearchHandler = useCallback(async () => {
    if (!IsSuppliers) return;

    try {
      const response = await searchProductSuppliers({
        ActiveBranchId,
        data: filters,
      }).unwrap();

      setData(response);
    } catch (error) {
      showNotification(error, 'error');
    }
  }, [
    IsSuppliers,
    ActiveBranchId,
    filters,
    searchProductSuppliers,
    showNotification,
  ]);

  const total = useMemo(() => data?.CountItems ?? 0, [data]);
  const tableData = useMemo(() => data?.Items ?? [], [data]);

  const { ItemsPerPage, Page } = filters;

  const range = useMemo(() => {
    const from = (Page - 1) * ItemsPerPage + 1;
    const to = Math.min(Page * ItemsPerPage, total);
    return { from, to };
  }, [Page, ItemsPerPage, total]);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm } }) => {
      setUnsavedFilters((prev) => ({ ...prev, SearchTerm }));
    },
    []
  );

  const skipSearchTerm = useCallback(() => {
    setUnsavedFilters((prev) => ({ ...prev, SearchTerm: '' }));
    setFilters((prevFilters) => ({ ...prevFilters, Page: 1, SearchTerm: '' }));
  }, []);

  const onSearch = useCallback(() => {
    const { SearchTerm } = unsavedFilters;
    setFilters((prevFilters) => ({ ...prevFilters, Page: 1, SearchTerm }));
  }, [unsavedFilters]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (Page: number) => {
      setFilters((prevFilters) => ({ ...prevFilters, Page }));
    },
    []
  );

  const onPageSizeChange = useCallback((ItemsPerPage: number) => {
    setFilters((prevFilters) => ({ ...prevFilters, ItemsPerPage, Page: 1 }));
  }, []);

  const onEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_SUPPLIER_PATH}?id=${id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => getSuppliersColumns({ onEdit, IsSupplierEdit }),
    [onEdit, IsSupplierEdit]
  );

  const isPageLoading = useMemo(
    () => isUninitialized || (isFetching && !data),
    [isFetching, isUninitialized, data]
  );

  const disabledSearch = useMemo(() => {
    const { SearchTerm } = unsavedFilters;
    return SearchTerm.length > 0 && SearchTerm.length < 3;
  }, [unsavedFilters]);

  return {
    filters,
    unsavedFilters,
    onPaginate,
    range,
    onPageSizeChange,
    columns,
    total,
    tableData,
    onChangeSearchTerm,
    skipSearchTerm,
    onSearch,
    onSearchHandler,
    isPageLoading,
    isFetching,
    isPageForbidden,
    disabledSearch,
  };
};
